/**
 * Export Service - Clean Architecture Export Implementation
 *
 * This service handles all export operations following clean architecture principles.
 * It provides a unified interface for exporting data in different formats.
 */

import {
  ExportServiceInterface,
  TableExportProps,
  ExportResult,
  ExportFormat,
  EXPORT_FORMAT_CONFIG,
  DEFAULT_EXPORT_OPTIONS
} from '../../types/export.types';
import {useNotificationStore} from '../../features/notifications/store/notificationStore';
import {excelAdapter} from './ExcelAdapter';

export class ExportService implements ExportServiceInterface {
  constructor () {
    // No initialization needed - using Zustand store directly
  }

  /**
   * Get notification store instance
   * Used for service classes that can't use React hooks
   */
  private getNotificationStore () {
    return useNotificationStore.getState();
  }

  /**
   * Export data to CSV format
   */
  async exportToCSV<T> (props: TableExportProps<T>): Promise<ExportResult> {
    try {
      const {data, config, options = DEFAULT_EXPORT_OPTIONS} = props;

      if (!data || data.length === 0) {
        return {
          success: false,
          error: 'لا توجد بيانات للتصدير',
          recordCount: 0
        };
      }

      // Prepare CSV content
      const csvContent = this.prepareCSVContent(data, config, options);

      // Create and download file
      const filename = this.generateFilename(config.filename, 'csv', options);
      await this.downloadFile(csvContent, filename, EXPORT_FORMAT_CONFIG.csv.mimeType);

      this.getNotificationStore().addNotification({
        type: 'success',
        message: 'تم تصدير البيانات بنجاح',
        details: `تم تصدير ${data.length} سجل إلى ملف CSV`,
        duration: 3000
      });

      return {
        success: true,
        filename,
        recordCount: data.length
      };

    } catch (error) {
      this.getNotificationStore().addNotification({
        type: 'error',
        message: 'خطأ في تصدير CSV',
        details: 'حدث خطأ أثناء تصدير البيانات',
        duration: 5000
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'خطأ غير معروف',
        recordCount: 0
      };
    }
  }

  /**
   * Export data to Excel format
   * Uses the Excel adapter for proper .xlsx file generation
   */
  async exportToExcel<T> (props: TableExportProps<T>): Promise<ExportResult> {
    try {
      const {data, config, options = DEFAULT_EXPORT_OPTIONS} = props;

      if (!data || data.length === 0) {
        return {
          success: false,
          error: 'لا توجد بيانات للتصدير',
          recordCount: 0
        };
      }

      // Generate proper Excel file using Excel adapter
      const excelBuffer = excelAdapter.generateExcelFile(data, config, options);
      const filename = this.generateFilename(config.filename, 'excel', options);

      // Download Excel file
      await this.downloadExcelFile(excelBuffer, filename);

      this.getNotificationStore().addNotification({
        type: 'success',
        message: 'تم تصدير البيانات بنجاح',
        details: `تم تصدير ${data.length} سجل إلى ملف Excel`,
        duration: 3000
      });

      return {
        success: true,
        filename,
        recordCount: data.length
      };

    } catch (error) {
      this.getNotificationStore().addNotification({
        type: 'error',
        message: 'خطأ في تصدير Excel',
        details: 'حدث خطأ أثناء تصدير البيانات',
        duration: 5000
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'خطأ غير معروف',
        recordCount: 0
      };
    }
  }


  /**
   * Prepare CSV content from data
   * Numeric values are exported as raw numbers without formatting for proper Excel handling
   */
  private prepareCSVContent<T> (
    data: T[],
    config: { columns: Array<{ key: keyof T; title: string; formatter?: (value: unknown, row: T) => string; hidden?: boolean }>; filename: string },
    options: { includeHeaders?: boolean; delimiter?: string; encoding?: string; includeTotals?: boolean; totalsLabel?: string }
  ): string {
    const {delimiter = ',', includeTotals = false} = options;
    const visibleColumns = config.columns.filter(col => !col.hidden);

    // Prepare headers
    const headers = visibleColumns.map(col => col.title);

    // Track numeric columns and their totals
    const numericColumnIndices: number[] = [];
    const columnTotals: number[] = new Array(visibleColumns.length).fill(0);

    // Prepare data rows
    const rows = data.map(row =>
      visibleColumns.map((col, colIndex) => {
        const value = row[col.key];
        const formattedValue = col.formatter ? col.formatter(value, row) : String(value || '');

        // Check if the formatted value is numeric
        const numericValue = this.parseNumericValue(formattedValue);
        if (numericValue !== null) {
          // Track this column as numeric
          if (!numericColumnIndices.includes(colIndex)) {
            numericColumnIndices.push(colIndex);
          }
          // Add to total
          columnTotals[colIndex] += numericValue;
          // Export numeric values without quotes for better Excel compatibility
          return String(numericValue);
        }

        // Escape CSV special characters for non-numeric values
        return this.escapeCSVValue(formattedValue);
      })
    );

    // Prepare totals row if requested
    let allRows: string[][];
    if (includeTotals && data.length > 0) {
      const totalsLabel = options.totalsLabel || 'الإجمالي';
      const totalsRow = visibleColumns.map((col, colIndex) => {
        if (numericColumnIndices.includes(colIndex)) {
          return String(columnTotals[colIndex]);
        }
        return colIndex === 0 ? totalsLabel : '';
      });
      allRows = options.includeHeaders ? [headers, ...rows, totalsRow] : [...rows, totalsRow];
    } else {
      allRows = options.includeHeaders ? [headers, ...rows] : rows;
    }

    const csvContent = allRows.map(row => row.join(delimiter)).join('\n');

    // Add BOM for proper Arabic encoding
    return options.encoding === 'utf-8-bom' ? `\ufeff${csvContent}` : csvContent;
  }

  /**
   * Parse string value to number if possible
   * Returns null if not a valid number
   */
  private parseNumericValue (value: string): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const trimmed = String(value).trim();
    const numValue = Number(trimmed);

    if (!isNaN(numValue) && isFinite(numValue)) {
      return numValue;
    }

    return null;
  }

  /**
   * Escape CSV special characters
   */
  private escapeCSVValue (value: string): string {
    const cleanValue = String(value).replace(/"/g, '""');
    return `"${cleanValue}"`;
  }

  /**
   * Generate filename with timestamp
   */
  private generateFilename (
    baseFilename: string,
    format: ExportFormat,
    options: { includeTimestamp?: boolean }
  ): string {
    const extension = EXPORT_FORMAT_CONFIG[format].extension;
    const timestamp = options.includeTimestamp
      ? `_${new Date().toISOString().split('T')[0]}_${new Date().toTimeString().split(' ')[0].replace(/:/g, '-')}`
      : '';

    return `${baseFilename}${timestamp}.${extension}`;
  }

  /**
   * Download file to user's device (for CSV and text-based files)
   */
  private async downloadFile (content: string, filename: string, mimeType: string): Promise<void> {
    const blob = new Blob([content], {type: mimeType});
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  }

  /**
   * Download Excel file to user's device (for binary files)
   */
  private async downloadExcelFile (buffer: ArrayBuffer, filename: string): Promise<void> {
    const blob = new Blob([buffer], {type: EXPORT_FORMAT_CONFIG.excel.mimeType});
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  }
}

// Export singleton instance
export const exportService = new ExportService();
